import L from 'leaflet'
import { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Polyline } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { errorApi } from '../../../../utils/errorApi'
import api from '../../../../services/api'
import { toast } from 'react-toastify'

import DotOutline from '../../../../assets/DotOutline.png'
import House from '../../../../assets/House.png'
import circleFill from '../../../../assets/circleFill.png'
import { calculateHaversineDistanceMeters, douglasPeucker } from '../../../../utils/distanceUtils'
import { Badge } from 'react-bootstrap'
import { useLoading } from '../../../../hooks/LoadingContext'

function DeliveryMap({ requestId, role }) {
  const [waypoints, setWaypoints] = useState([])

  const { setIsLoading } = useLoading()

  const [zoom, setZoom] = useState(15)
  const boundsFitted = useRef(false)

  useEffect(() => {
    async function loadWaypointsData() {
      try {
        setIsLoading(true)
        const { data } = await api.get(`/${role}/requests/${requestId}/waypoints`)
        const waypoints = data.waypoints.map((w) => [w.lat, w.lng])
        boundsFitted.current = false

        const newWaypoints = getReducedWaypoints(waypoints)
        setWaypoints(newWaypoints)
      } catch (e) {
        console.error(e)
        toast.error(
          errorApi(
            e,
            'Falha ao carregar dados a respeito do caminho da entrega. Tente novamente mais tarde.',
          ),
        )
      }
      setIsLoading(false)
    }
    loadWaypointsData()
  }, [requestId, role, setIsLoading])

  function getReducedWaypoints(waypoints) {
    let index = 0
    const waypointChunks = []

    // splits waypoints into chunks if there are disconnected points
    for (const [i] of waypoints.entries()) {
      if (
        calculateHaversineDistanceMeters(waypoints[i], waypoints[i + 1]) > 100 &&
        waypoints.slice(index, i + 1).length > 0
      ) {
        waypointChunks.push(waypoints.slice(index, i + 1))
        index = i + 1
      }
    }
    waypointChunks.push(waypoints.slice(index))

    const newWaypoints = []

    for (const waypoint of waypointChunks) {
      newWaypoints.push(douglasPeucker(waypoint, 0.00001))
    }

    return newWaypoints
  }

  function Markers({ waypoints, zoomLevel }) {
    const map = useMap()

    if (!boundsFitted.current) {
      boundsFitted.current = true
      if (waypoints?.length > 0) map.fitBounds(waypoints)
    }

    let markerIcon = L.icon({
      iconUrl: DotOutline,
      iconSize: [zoomLevel * 2, zoomLevel * 2],
    })

    let houseIcon = L.icon({
      iconUrl: House,
      iconSize: [zoomLevel * 2, zoomLevel * 2],
    })

    let circleIcon = L.icon({
      iconUrl: circleFill,
      iconSize: [zoomLevel * 0.6, zoomLevel * 0.6],
    })

    return (
      <>
        {waypoints?.map((waypoint, index) => {
          if (waypoint.length === 0) {
            return null
          } else if (waypoint.length === 1) {
            return <Marker key={'wp_' + index} position={waypoint[0]} icon={circleIcon} />
          } else {
            return <Polyline key={index} color={`#053C5E`} positions={waypoint} />
          }
        })}
        <Marker key={'wp_' + 0} position={waypoints[0][0]} icon={houseIcon} />
        <Marker key={'wp_' + 1} position={waypoints.at(-1).at(-1)} icon={markerIcon} />
      </>
    )
  }

  function Map({ setZoom }) {
    const mapEvents = useMapEvents({
      zoomend: () => {
        setZoom(mapEvents.getZoom())
      },
    })
    return null
  }

  return (
    <>
      {waypoints[0]?.length > 0 ? (
        <MapContainer zoom={zoom} style={{ borderRadius: 15 }}>
          <Map setZoom={setZoom} />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {waypoints[0]?.length > 0 && <Markers waypoints={waypoints} zoomLevel={zoom}></Markers>}
          {/* <ZoomControl position="bottomright" /> */}
        </MapContainer>
      ) : (
        <div className="text-center mt-2 mb-3">
          <Badge
            bg="#053C5E"
            style={{
              maxWidth: 'fit-content',
              backgroundColor: '#053C5E',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            O trajeto da entrega não foi encontrado.
          </Badge>
        </div>
      )}
    </>
  )
}

export default DeliveryMap
